import {
  type DisplayZone,
  type FormatOptions,
  type ResolvedDateTimeFormat,
} from "./format";
import { getActiveDateTimeFormat } from "./registry";

/**
 * Convenience formatters that read the active date/time preference from the
 * registry. Use these in pure (non-React) code; React components should prefer
 * the `useDateTimeFormat` hook so they re-render when the preference changes.
 *
 * All accept a `Date`, an epoch-millisecond number, or a date string / ISO
 * value (anything `new Date(...)` understands). Invalid inputs return the
 * `fallback` (default `""`) so a single bad value can't throw inside a table
 * cell or chart axis.
 */

export type DateInput = Date | string | number | null | undefined;

/** Coerce a {@link DateInput} into a valid `Date`, or `null`. */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatWith(
  value: DateInput,
  options: FormatOptions,
  fallback: string,
): string {
  const date = toDate(value);
  if (date === null) {
    return fallback;
  }
  return getActiveDateTimeFormat().format(date, options);
}

/** Date only, honoring the active preset (e.g. `30.06.2026`). */
export function formatDate(value: DateInput, fallback = ""): string {
  return formatWith(value, { time: "none" }, fallback);
}

/**
 * Below this age a timestamp reads as relative ("3 minutes ago"); at or past
 * it, as a date. Shared by every surface that shows a created/modified time.
 */
export const RECENT_THRESHOLD_MS = 24 * 60 * 60 * 1000;

const relativeFmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

/**
 * Locale-aware relative phrasing: "now", "3 minutes ago", "2 hours ago".
 *
 * Deliberately not run through the date preference: a date-ordering/clock
 * preset says nothing about relative wording, so it stays locale-driven.
 * `Intl.RelativeTimeFormat` localizes a (value, unit) pair without picking the
 * unit, so this buckets into now / minutes / hours only; callers switch to a
 * date once {@link isRecent} is false. Floored to minutes, matching the
 * 60-second tick relative displays re-render on.
 */
export function formatRelativeTime(value: DateInput, fallback = ""): string {
  const date = toDate(value);
  if (date === null) {
    return fallback;
  }
  const mins = Math.round((date.getTime() - Date.now()) / 60_000);
  if (mins > -1) {
    return relativeFmt.format(0, "second");
  }
  if (mins > -60) {
    return relativeFmt.format(mins, "minute");
  }
  return relativeFmt.format(Math.round(mins / 60), "hour");
}

/** Whether `value` is within {@link RECENT_THRESHOLD_MS} of now. */
export function isRecent(value: DateInput): boolean {
  const date = toDate(value);
  return date !== null && Date.now() - date.getTime() < RECENT_THRESHOLD_MS;
}

/** Date + `HH:mm`, honoring the active preset and clock. */
export function formatDateTime(value: DateInput, fallback = ""): string {
  return formatWith(value, { time: "minutes" }, fallback);
}

/** Date + `HH:mm:ss`. */
export function formatDateTimeWithSeconds(
  value: DateInput,
  fallback = "",
): string {
  return formatWith(value, { time: "seconds" }, fallback);
}

/**
 * The active format itself, reading instants in `zone` (the viewer's local
 * zone when omitted), for pure code that needs more than the helpers here:
 * `formatParts`, `formatFractional`, or the preference it was resolved from.
 */
export function activeDateTimeFormat(
  zone?: DisplayZone,
): ResolvedDateTimeFormat {
  return getActiveDateTimeFormat(zone);
}

/**
 * Escape hatch for callers that need full control over the granularity and
 * timezone display in one call — including millisecond precision
 * (`{ time: "millis" }`), which has no named helper.
 */
export function formatDateTimeWithOptions(
  value: DateInput,
  options: FormatOptions,
  fallback = "",
): string {
  return formatWith(value, options, fallback);
}

/**
 * "Oct 29": a short date, with the year only when it isn't this year's, all
 * read in one zone.
 */
interface ShortDateFormats {
  monthDay: Intl.DateTimeFormat;
  monthDayYear: Intl.DateTimeFormat;
  year: Intl.DateTimeFormat;
}

/** One {@link ShortDateFormats} per zone; `undefined` is the viewer's local zone. */
const SHORT_DATE_FORMATS = new Map<DisplayZone | undefined, ShortDateFormats>();

function shortDateFormats(zone: DisplayZone | undefined): ShortDateFormats {
  let formats = SHORT_DATE_FORMATS.get(zone);
  if (formats === undefined) {
    const monthDay: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      timeZone: zone,
    };
    formats = {
      monthDay: new Intl.DateTimeFormat(undefined, monthDay),
      monthDayYear: new Intl.DateTimeFormat(undefined, {
        ...monthDay,
        year: "numeric",
      }),
      year: new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        timeZone: zone,
      }),
    };
    SHORT_DATE_FORMATS.set(zone, formats);
  }
  return formats;
}

/**
 * "Oct 29, 11:32 AM": short enough for a table column on one line. The time
 * follows `format`'s 12/24-hour preference; the date is always written with
 * a short month, whatever the preset. Both are read in `format`'s zone, as is
 * whether the instant falls in this year.
 */
export function formatShortDateTime(
  value: DateInput,
  format: ResolvedDateTimeFormat = getActiveDateTimeFormat(),
  fallback = "",
): string {
  const date = toDate(value);
  if (date === null) {
    return fallback;
  }
  const { monthDay, monthDayYear, year } = shortDateFormats(format.zone);
  const day = (
    year.format(date) === year.format(new Date()) ? monthDay : monthDayYear
  ).format(date);
  return `${day}, ${format.formatParts(date).time}`;
}
